// M4 Squat Gate — squat down to pass under high bars sliding right→left.
// Spec: /microgames/04-squat-gate.md

import type {
  GameContext,
  InputFrame,
  Microgame,
  MicrogameResult,
} from '@pose-game/core';

const DURATION_SEC = 22;
const CALIBRATION_SEC = 1.0;
const TOTAL_BARS = 5;
const BAR_SPACING_SEC = 3.5;
const BAR_SPEED_PX_PER_SEC_AT_1080 = 160;
const SQUAT_THRESHOLD = 0.55;
const REFERENCE_WIDTH = 1080;

interface Bar {
  /** Spawn time in seconds since game start (when bar begins to scroll on at right edge). */
  spawnAt: number;
  /** Has this bar been judged at the moment its center crossed the midline. */
  judged: boolean;
  /** Result of the judgement (only meaningful when judged). */
  passed: boolean;
}

interface State {
  ctx: GameContext;
  elapsed: number;
  bars: Bar[];
  passes: number;
  hits: number;
  /** Latest squat level seen this frame for rendering the player silhouette. */
  squatLevel: number;
  /** Audio cue debounce — last bar index sfx'd. */
  lastSfxBar: number;
}

let state: State | null = null;

function buildBars(): Bar[] {
  const bars: Bar[] = [];
  for (let i = 0; i < TOTAL_BARS; i++) {
    bars.push({
      spawnAt: CALIBRATION_SEC + i * BAR_SPACING_SEC,
      judged: false,
      passed: false,
    });
  }
  return bars;
}

/**
 * Convert (elapsed since spawnAt, canvasWidth) → x of bar center.
 * Bars enter at right edge (x = w + barWidth/2) and scroll left.
 */
function barCenterX(timeSinceSpawn: number, canvasWidth: number): number {
  const speed = (BAR_SPEED_PX_PER_SEC_AT_1080 * canvasWidth) / REFERENCE_WIDTH;
  return canvasWidth - timeSinceSpawn * speed;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export const squatGate: Microgame = {
  name: '04-squat-gate',
  durationSec: DURATION_SEC,
  requires: { pose: true },

  init(gctx: GameContext) {
    state = {
      ctx: gctx,
      elapsed: 0,
      bars: buildBars(),
      passes: 0,
      hits: 0,
      squatLevel: 0,
      lastSfxBar: -1,
    };
  },

  tick(dt: number, frame: InputFrame) {
    if (state == null) return;
    state.elapsed += dt;
    state.squatLevel = clamp01(frame.pose.squatLevel);

    const w = frame.videoWidth || state.ctx.canvas.width;
    const midX = w / 2;

    for (let i = 0; i < state.bars.length; i++) {
      const bar = state.bars[i];
      if (bar == null || bar.judged) continue;
      if (state.elapsed < bar.spawnAt) continue;
      const tSince = state.elapsed - bar.spawnAt;
      const x = barCenterX(tSince, w);
      if (x <= midX) {
        bar.judged = true;
        const squatting = state.squatLevel > SQUAT_THRESHOLD;
        bar.passed = squatting;
        if (squatting) {
          state.passes += 1;
          if (state.lastSfxBar !== i) {
            state.ctx.audio.pop();
            state.lastSfxBar = i;
          }
        } else {
          state.hits += 1;
          if (state.lastSfxBar !== i) {
            state.ctx.audio.thud();
            state.lastSfxBar = i;
          }
        }
      }
    }
  },

  render(g: CanvasRenderingContext2D) {
    if (state == null) return;
    const { canvas } = state.ctx;
    const w = canvas.width;
    const h = canvas.height;

    // Gradient sky → ground.
    const sky = g.createLinearGradient(0, 0, 0, h);
    sky.addColorStop(0, '#0b1330');
    sky.addColorStop(0.55, '#1b2452');
    sky.addColorStop(0.55, '#2a1c3a');
    sky.addColorStop(1, '#120a1e');
    g.fillStyle = sky;
    g.fillRect(0, 0, w, h);

    // Horizon line.
    const horizonY = h * 0.62;
    g.strokeStyle = 'rgba(255, 200, 120, 0.35)';
    g.lineWidth = Math.max(1, h * 0.002);
    g.beginPath();
    g.moveTo(0, horizonY);
    g.lineTo(w, horizonY);
    g.stroke();

    // Bars — scrolling right→left. Positioned in the upper half of the screen.
    const barThickness = Math.max(14, h * 0.04);
    const barY = h * 0.28;
    const barWidth = w * 0.18;
    for (const bar of state.bars) {
      if (state.elapsed < bar.spawnAt) continue;
      const tSince = state.elapsed - bar.spawnAt;
      const cx = barCenterX(tSince, w);
      if (cx < -barWidth) continue;
      const left = cx - barWidth / 2;

      // Glow.
      g.fillStyle = 'rgba(120, 220, 255, 0.18)';
      g.fillRect(left - 6, barY - barThickness / 2 - 6, barWidth + 12, barThickness + 12);

      // Body.
      g.fillStyle = '#1c2a44';
      g.fillRect(left, barY - barThickness / 2, barWidth, barThickness);

      // Neon stroke — color changes after judging.
      let stroke = '#64e8ff';
      if (bar.judged) stroke = bar.passed ? '#7dffb1' : '#ff5d7a';
      g.strokeStyle = stroke;
      g.lineWidth = Math.max(2, h * 0.005);
      g.strokeRect(left, barY - barThickness / 2, barWidth, barThickness);

      // Support posts hinting it's a "gate".
      g.fillStyle = '#0e1730';
      g.fillRect(left + 6, barY + barThickness / 2, 6, h - (barY + barThickness / 2));
      g.fillRect(left + barWidth - 12, barY + barThickness / 2, 6, h - (barY + barThickness / 2));
    }

    // Player silhouette — left third, capsule head + body that lowers with squatLevel.
    const playerX = w * 0.22;
    const standingHeadY = h * 0.42;
    const maxDrop = h * 0.18;
    const headY = standingHeadY + state.squatLevel * maxDrop;
    const headR = Math.max(18, h * 0.045);
    const footY = h * 0.78;

    // Body capsule.
    g.fillStyle = '#23314d';
    g.beginPath();
    const bodyHalfW = headR * 0.85;
    g.moveTo(playerX - bodyHalfW, headY);
    g.lineTo(playerX - bodyHalfW, footY);
    g.lineTo(playerX + bodyHalfW, footY);
    g.lineTo(playerX + bodyHalfW, headY);
    g.closePath();
    g.fill();
    g.strokeStyle = '#7ad0ff';
    g.lineWidth = Math.max(2, h * 0.004);
    g.stroke();

    // Head.
    g.fillStyle = '#2e3f63';
    g.beginPath();
    g.arc(playerX, headY, headR, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = '#9be1ff';
    g.lineWidth = Math.max(2, h * 0.004);
    g.stroke();

    // HUD top.
    g.fillStyle = '#fff';
    g.textAlign = 'left';
    g.textBaseline = 'top';
    g.font = `bold ${Math.round(h * 0.035)}px system-ui, sans-serif`;
    g.fillText(`PASSED ${state.passes} / ${TOTAL_BARS}`, w * 0.03, h * 0.03);

    const remain = Math.max(0, DURATION_SEC - state.elapsed);
    g.textAlign = 'right';
    g.fillStyle = '#cfd5ee';
    g.fillText(`${remain.toFixed(1)}s`, w * 0.97, h * 0.03);

    // Calibration banner during first second.
    if (state.elapsed < CALIBRATION_SEC) {
      const bannerH = h * 0.14;
      const bannerY = h * 0.5 - bannerH / 2;
      g.fillStyle = 'rgba(10, 14, 30, 0.78)';
      g.fillRect(0, bannerY, w, bannerH);
      g.fillStyle = '#ffd86b';
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.font = `bold ${Math.round(h * 0.05)}px system-ui, sans-serif`;
      g.fillText('STAND STILL — CALIBRATING', w / 2, h * 0.5 - h * 0.018);
      g.fillStyle = '#cfd5ee';
      g.font = `${Math.round(h * 0.028)}px system-ui, sans-serif`;
      g.fillText('squat under the bars when they reach the center', w / 2, h * 0.5 + h * 0.03);
    }
  },

  end(): MicrogameResult {
    const s = state;
    if (s == null) {
      return {
        microgame: '04-squat-gate',
        won: false,
        score: 0,
        detail: { hits: 0, totalBars: TOTAL_BARS },
      };
    }
    // Anything not judged (e.g. game cut short) counts as a miss for accounting.
    let hits = s.hits;
    let passes = s.passes;
    for (const bar of s.bars) {
      if (!bar.judged) hits += 1;
    }
    const won = passes >= 4;
    return {
      microgame: '04-squat-gate',
      won,
      score: passes,
      detail: { hits, totalBars: TOTAL_BARS },
    };
  },
};
